import React, { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, ArrowRight, Briefcase, CheckCircle } from 'lucide-react'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'

type Answers = Record<string, string>

export type OfferQuestionnaireVariant = 'create' | 'regenerate'

export type OfferQuestionnaireAnswerPatch = {
  field: string
  value: string
  token: number
}

interface OfferQuestionnaireWizardProps {
  language: string
  initialJobTitle?: string
  initialExperienceLevel?: string
  /** Prefill all questionnaire fields (used by regenerate). */
  initialAnswers?: Record<string, string>
  variant?: OfferQuestionnaireVariant
  /** Apply a recommendation into the matching field and jump to its step. */
  answerPatch?: OfferQuestionnaireAnswerPatch | null
  disabled?: boolean
  completed?: boolean
  onSubmit: (
    message: string,
    display: { title: string; answers: Array<{ field: string; label: string; value: string }> }
  ) => Promise<void> | void
  /** Regenerated draft submit — raw string answers from the same UI cards. */
  onRegenerateSubmit?: (answers: Record<string, string>) => Promise<void> | void
}

/** Guided creation: one step only — titre + commentaire → Générer l'offre. */
const CREATE_STEPS = [
  { key: 'configure', fields: ['job_title', 'comment'] },
] as const

const REGENERATE_STEPS = [
  { key: 'title', fields: ['job_title'] },
  { key: 'basics', fields: ['department', 'contract_type'] },
  { key: 'experience', fields: ['experience_level', 'education_level'] },
  { key: 'requirements', fields: ['requirements', 'main_missions'] },
  { key: 'description', fields: ['description_text'] },
  { key: 'missions', fields: ['soft_skills'] },
  { key: 'extras', fields: ['benefits', 'languages', 'certifications'] },
  { key: 'remote', fields: ['remote_allowed'] },
] as const

const LABELS: Record<string, { fr: string; en: string; placeholder?: { fr: string; en: string } }> = {
  job_title: { fr: 'Titre du poste', en: 'Job title', placeholder: { fr: 'Ex. Responsable commercial', en: 'E.g. Sales Manager' } },
  comment: {
    fr: 'Commentaire',
    en: 'Comment',
    placeholder: {
      fr: 'Précisez le contexte, le profil recherché, les contraintes…',
      en: 'Add context, desired profile, constraints…',
    },
  },
  department: { fr: 'Département', en: 'Department', placeholder: { fr: 'Ex. Commercial, Finance, Opérations…', en: 'E.g. Sales, Finance, Operations…' } },
  contract_type: { fr: 'Type de contrat', en: 'Contract type' },
  experience_level: { fr: "Années d’expérience", en: 'Years of experience', placeholder: { fr: 'Ex. 3', en: 'E.g. 3' } },
  education_level: { fr: "Niveau d’études requis", en: 'Required education level', placeholder: { fr: 'Ex. Bac+3, Bac+5…', en: "E.g. Bachelor's, Master's…" } },
  description_text: { fr: 'Description du poste', en: 'Job description', placeholder: { fr: 'Décrivez le contexte, les objectifs et les responsabilités du poste…', en: 'Describe the role context, goals, and responsibilities…' } },
  requirements: { fr: 'Compétences techniques', en: 'Technical skills', placeholder: { fr: 'Listez les compétences et outils requis…', en: 'List the required skills and tools…' } },
  soft_skills: { fr: 'Soft-skills', en: 'Soft skills', placeholder: { fr: 'Ex. communication, autonomie, organisation…', en: 'E.g. communication, autonomy, organization…' } },
  main_missions: { fr: 'Missions principales', en: 'Main responsibilities', placeholder: { fr: 'Décrivez les principales missions du poste…', en: 'Describe the role’s main responsibilities…' } },
  benefits: { fr: 'Souhaitables (nice to have)', en: 'Nice to have', placeholder: { fr: 'Ex. expérience sectorielle, outil secondaire…', en: 'E.g. industry experience, secondary tool…' } },
  languages: { fr: 'Langues et niveaux', en: 'Languages and levels', placeholder: { fr: 'Ex. Français : courant, Anglais : B2', en: 'E.g. French: fluent, English: B2' } },
  certifications: { fr: 'Certifications', en: 'Certifications', placeholder: { fr: 'Listez les certifications requises ou souhaitées…', en: 'List required or preferred certifications…' } },
  remote_allowed: { fr: 'Télétravail autorisé', en: 'Remote work allowed' },
}

const STEP_TITLES: Record<'fr' | 'en', Record<string, string>> = {
  fr: {
    configure: 'Configurer l’offre',
    title: 'Titre du poste',
    basics: 'Département et contrat',
    experience: 'Expérience et études',
    requirements: 'Compétences et missions essentielles',
    description: 'Description',
    missions: 'Soft-skills',
    extras: 'Avantages, langues et certifications',
    remote: 'Télétravail',
  },
  en: {
    configure: 'Configure the offer',
    title: 'Job title',
    basics: 'Department and contract',
    experience: 'Experience and education',
    requirements: 'Essential skills and responsibilities',
    description: 'Description',
    missions: 'Soft skills',
    extras: 'Benefits, languages and certifications',
    remote: 'Remote work',
  },
}

const OFFER_QUESTIONNAIRE_SIZE_LIMIT = 200_000

export const isOfferQuestionnaireResponse = (content: string): boolean => {
  // Legacy fallback for messages created before offer_setup metadata existed.
  // Skip expensive normalization for very large content — questionnaire prompts are short
  if (content.length > OFFER_QUESTIONNAIRE_SIZE_LIMIT) return false
  const normalized = content.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  const guidedSetupCopy = (
    normalized.includes('choisissez comment vous souhaitez configurer cette offre')
    || normalized.includes('choose how you would like to configure this job offer')
  )
  const asksForTitle = normalized.includes('quel est le titre du poste') || normalized.includes('what is the job title')
  const asksForBaseDetails = normalized.includes('informations de base') && normalized.includes('type de contrat')
  const asksForCompleteOffer = normalized.includes('rediger une offre complete') && normalized.includes('competences requises')
  const fullQuestionnaire = normalized.includes('departement') && normalized.includes('teletravail') && normalized.includes('voulez-vous')
  const genericOfferTemplate = normalized.includes("creation d’une offre d’emploi") && normalized.includes('modele') && normalized.includes('intitule du poste')
  return guidedSetupCopy || asksForTitle || asksForBaseDetails || asksForCompleteOffer || fullQuestionnaire || genericOfferTemplate
}

/** @deprecated Prefer message.metadata.offer_setup === 'guided' from OfferSetupDetectionAgent. */
export const isSimpleNewOfferRequest = (content: string): boolean => {
  if (content.length > OFFER_QUESTIONNAIRE_SIZE_LIMIT) return false
  const normalized = content
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[’']/g, ' ')
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  const referencesOffer = /\b(offre|offer)\b/.test(normalized)
  const requestsCreation = /\b(create|generate|generer|genere|creer|cree|creation|nouvelle|new|autre|another)\b/.test(normalized)
  const looksLikeDetailedBrief = normalized.length > 180 || /\b(requirements|competences|missions|responsabilites|description complete|salaire|salary|avantages|benefits)\b/.test(normalized)
  return referencesOffer && requestsCreation && !looksLikeDetailedBrief
}

export function OfferQuestionnaireWizard({
  language,
  initialJobTitle = '',
  initialExperienceLevel = '',
  initialAnswers,
  variant = 'create',
  answerPatch = null,
  disabled,
  completed,
  onSubmit,
  onRegenerateSubmit,
}: OfferQuestionnaireWizardProps) {
  const locale = language.startsWith('fr') ? 'fr' : 'en'
  const isRegenerate = variant === 'regenerate'
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<Answers>(() => ({
    ...(initialAnswers || {}),
    ...(initialJobTitle.trim() ? { job_title: initialJobTitle.trim() } : {}),
    ...(initialExperienceLevel.trim() ? { experience_level: initialExperienceLevel.trim() } : {}),
  }))
  const [submitting, setSubmitting] = useState(false)
  const [highlightedField, setHighlightedField] = useState<string | null>(null)

  useEffect(() => {
    if (!answerPatch?.field) return
    setAnswers((previous) => ({ ...previous, [answerPatch.field]: answerPatch.value }))
    setHighlightedField(answerPatch.field)
    const targetStep = REGENERATE_STEPS.findIndex((item) => (
      (item.fields as readonly string[]).includes(answerPatch.field)
    ))
    if (targetStep >= 0) setStep(targetStep)
  }, [answerPatch?.token, answerPatch?.field, answerPatch?.value])

  const activeSteps = useMemo(() => {
    if (isRegenerate) {
      return REGENERATE_STEPS.map((item) => ({ ...item, fields: [...item.fields] }))
    }
    return CREATE_STEPS.map((item) => ({ ...item, fields: [...item.fields] }))
  }, [isRegenerate])

  const current = activeSteps[step] || activeSteps[0]
  const canContinue = Boolean(answers.job_title?.trim())
  const visibleStepCount = activeSteps.length
  const progress = ((step + 1) / Math.max(visibleStepCount, 1)) * 100

  const submission = useMemo(() => {
    const jobTitle = (answers.job_title || '').trim()
    const comment = (answers.comment || '').trim()
    const lines = [
      ...(jobTitle ? [`- job_title: ${jobTitle}`] : []),
      ...(comment ? [`- comment: ${comment}`] : []),
      '- generation_mode: ai',
    ]
    const heading = locale === 'fr'
      ? "Voici mes réponses pour générer l'offre :"
      : 'Here are my answers to generate the offer:'
    const completionInstruction = locale === 'fr'
      ? "UI_OFFER_COMPLETION_MODE=AI_GENERATE. Tu es explicitement autorisé à rédiger une offre complète et professionnelle à partir du titre et du commentaire. Ne pose aucune autre question : produis maintenant l'offre complète et offer_data."
      : 'UI_OFFER_COMPLETION_MODE=AI_GENERATE. You are explicitly authorized to draft a complete professional offer from the title and comment. Ask no more questions: produce the complete offer and offer_data now.'
    return `${heading}\n${lines.join('\n')}\n- instruction: ${completionInstruction}`
  }, [answers.comment, answers.job_title, locale])

  const update = (key: string, value: string) => {
    setHighlightedField(null)
    setAnswers((previous) => ({ ...previous, [key]: value }))
  }

  const submit = async () => {
    setSubmitting(true)
    try {
      if (isRegenerate && onRegenerateSubmit) {
        await onRegenerateSubmit(answers)
        return
      }
      const displayAnswers = [
        ...(answers.job_title?.trim()
          ? [{ field: 'job_title', label: LABELS.job_title[locale], value: answers.job_title.trim() }]
          : []),
        ...(answers.comment?.trim()
          ? [{ field: 'comment', label: LABELS.comment[locale], value: answers.comment.trim() }]
          : []),
      ]
      await onSubmit(submission, {
        title: locale === 'fr' ? "Configurer l'offre" : 'Configure the offer',
        answers: displayAnswers,
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (completed) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:border-green-900 dark:bg-green-950/30 dark:text-green-300">
        <CheckCircle className="h-4 w-4" />
        {locale === 'fr' ? 'Questionnaire envoyé.' : 'Questionnaire submitted.'}
      </div>
    )
  }

  if (!current) return null

  return (
    <div className="overflow-hidden rounded-xl border border-primary/20 bg-background shadow-sm">
      <div className="border-b border-border bg-primary/5 px-5 py-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">
              {isRegenerate
                ? (locale === 'fr' ? 'Modifier l’offre' : 'Edit the offer')
                : (locale === 'fr' ? "Configurer l’offre" : 'Configure the offer')}
            </h3>
          </div>
          {isRegenerate ? (
            <span className="text-xs font-medium text-muted-foreground">{step + 1} / {visibleStepCount}</span>
          ) : null}
        </div>
        {isRegenerate ? (
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
        ) : null}
      </div>

      <div className="space-y-4 p-5">
        <div>
          <h4 className="font-semibold text-foreground">{STEP_TITLES[locale][current.key]}</h4>
          <p className="mt-1 text-xs text-muted-foreground">
            {isRegenerate
              ? (locale === 'fr'
                ? 'Même formulaire que la création. Appliquez une recommandation ou modifiez manuellement.'
                : 'Same form as creation. Apply a recommendation or edit manually.')
              : (locale === 'fr'
                ? 'Indiquez le titre du poste et un commentaire optionnel, puis générez l’offre.'
                : 'Enter the job title and an optional comment, then generate the offer.')}
          </p>
        </div>

        {current.fields.map((field) => {
          const label = LABELS[field]
          const fieldShell = highlightedField === field
            ? 'rounded-xl border border-primary/50 bg-primary/5 p-3'
            : ''
          if (field === 'contract_type') {
            return (
              <label key={field} className={`block space-y-1.5 text-sm font-medium ${fieldShell}`}>
                {label[locale]}
                <select value={answers[field] || ''} onChange={(event) => update(field, event.target.value)} className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                  <option value="">{locale === 'fr' ? 'Non renseigné' : 'Not specified'}</option>
                  {['CDI', 'CDD', 'Stage', 'Freelance', 'Consultant', 'Temps partiel'].map((option) => <option key={option}>{option}</option>)}
                </select>
              </label>
            )
          }
          if (field === 'remote_allowed') {
            return (
              <div key={field} className={`space-y-2 ${fieldShell}`}>
                <p className="text-sm font-medium">{label[locale]}</p>
                <div className="grid grid-cols-3 gap-2">
                  {[['true', locale === 'fr' ? 'Oui' : 'Yes'], ['false', locale === 'fr' ? 'Non' : 'No'], ['', locale === 'fr' ? 'Non renseigné' : 'Not specified']].map(([value, text]) => (
                    <button key={text} type="button" onClick={() => update(field, value)} className={`rounded-lg border px-3 py-2 text-sm ${answers[field] === value ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'}`}>{text}</button>
                  ))}
                </div>
              </div>
            )
          }
          const multiline = [
            'comment',
            'description_text',
            'main_missions',
            'requirements',
            'soft_skills',
            'benefits',
            'languages',
            'certifications',
          ].includes(field)
          return (
            <label key={field} className={`block space-y-1.5 text-sm font-medium ${fieldShell}`}>
              {label[locale]}
              {field === 'comment' ? (
                <span className="ml-1 text-xs font-normal text-muted-foreground">
                  {locale === 'fr' ? '(optionnel)' : '(optional)'}
                </span>
              ) : null}
              {multiline ? (
                <textarea value={answers[field] || ''} onChange={(event) => update(field, event.target.value)} placeholder={label.placeholder?.[locale]} rows={field === 'comment' ? 3 : 4} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
              ) : (
                <Input type={field === 'experience_level' ? 'number' : 'text'} min={field === 'experience_level' ? 0 : undefined} value={answers[field] || ''} onChange={(event) => update(field, event.target.value)} placeholder={label.placeholder?.[locale]} className="mt-1" />
              )}
            </label>
          )
        })}

        <div className="flex items-center justify-between border-t border-border pt-4">
          {isRegenerate ? (
            <Button type="button" variant="ghost" onClick={() => setStep((value) => Math.max(0, value - 1))} disabled={step === 0 || disabled}>
              <ArrowLeft className="mr-2 h-4 w-4" /> {locale === 'fr' ? 'Retour' : 'Back'}
            </Button>
          ) : <span />}
          {!isRegenerate ? (
            <Button type="button" onClick={submit} disabled={disabled || submitting || !canContinue} loading={submitting}>
              {locale === 'fr' ? 'Générer l’offre' : 'Generate offer'}
            </Button>
          ) : step < activeSteps.length - 1 ? (
            <Button
              type="button"
              onClick={() => setStep((value) => value + 1)}
              disabled={disabled || !canContinue}
            >
              {locale === 'fr' ? 'Suivant' : 'Next'} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button type="button" onClick={submit} disabled={disabled || submitting} loading={submitting}>
              {locale === 'fr' ? 'Valider le brouillon' : 'Apply draft'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
